import type { CompanyDiscoveryAiOutput } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type {
  PrimarySelection,
  TreeFilter,
  TreeSelection,
} from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';

type ResolvedIndustryItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
  parentK10Id?: string;
  parentK10Name?: string;
};
type ResolvedGeographyItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
};
type ResolvedRoleItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  selectionListId?: string;
};

const PRIMARY_INDUSTRY_FILTER_ID = FilterId.COMPANY;
const PRIMARY_GEOGRAPHY_FILTER_ID = FilterId.GEOGRAPHY;
const PRIMARY_ROLE_FILTER_ID = FilterId.ROLE;

function buildSelectionsFromIndustry(
  industry: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['industry']
  >
): TreeSelection[] {
  const items = industry as ResolvedIndustryItem[];
  const definition = getFilterDefinition(PRIMARY_INDUSTRY_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];

  const normalized: { id: string; name: string; levelId: string }[] = [];
  for (const item of items) {
    const levelId = item.levelId ?? selectionListIds[0];
    if (levelId === 'rics_k50' && item.parentK10Id !== undefined) {
      normalized.push({
        id: `rics_k10.${item.parentK10Id}`,
        name: item.parentK10Name ?? item.name,
        levelId: 'rics_k10',
      });
    } else {
      const id = item.id ?? item.name;
      normalized.push({
        id,
        name: item.name,
        levelId: levelId as string,
      });
    }
  }

  const seenKey = new Set<string>();
  const selections: TreeSelection[] = [];
  for (const n of normalized) {
    if (!selectionListIds.includes(n.levelId)) continue;
    const key = `${n.levelId}:${n.id}`;
    if (seenKey.has(key)) continue;
    seenKey.add(key);
    selections.push({
      id: n.id,
      name: n.name,
      levelId: n.levelId as TreeSelection['levelId'],
    });
  }
  return selections;
}

function buildSelectionsFromGeography(
  geography: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['geography']
  >
): TreeSelection[] {
  const items = geography as ResolvedGeographyItem[];
  const definition = getFilterDefinition(PRIMARY_GEOGRAPHY_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
  const selections: TreeSelection[] = [];
  for (const item of items) {
    const id = item.id ?? item.name;
    const levelId =
      (item.levelId as TreeSelection['levelId']) ?? selectionListIds[0];
    if (selectionListIds.includes(levelId)) {
      selections.push({ id, name: item.name, levelId });
    }
  }
  return selections;
}

function buildSelectionsFromRole(
  role: NonNullable<
    CompanyDiscoveryAiOutput['filters']['primary_selector']['role']
  >
): TreeSelection[] {
  const items = role as ResolvedRoleItem[];
  const definition = getFilterDefinition(PRIMARY_ROLE_FILTER_ID);
  if (!definition || definition.type !== FilterType.TREE) return [];
  const selectionListIds =
    (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
  const selections: TreeSelection[] = [];
  for (const item of items) {
    const id = item.id ?? item.name;
    const levelId = (item.selectionListId ??
      selectionListIds[0]) as TreeSelection['levelId'];
    if (selectionListIds.includes(levelId)) {
      selections.push({ id, name: item.name, levelId });
    }
  }
  return selections;
}

/**
 * Converts CompanyDiscoveryAiOutput primary_selector (industry / geography / role)
 * into a single primary TreeFilter. Uses the first non-empty field to determine
 * which primary filter to build; uses resolved id/levelId or id/selectionListId from backend.
 */
export const transformPrimarySelector = (
  primary_selector: CompanyDiscoveryAiOutput['filters']['primary_selector']
): TreeFilter | null => {
  if (!primary_selector) return null;

  const industry = primary_selector.industry;
  const geography = primary_selector.geography;
  const role = primary_selector.role;

  const hasIndustry = Array.isArray(industry) && industry.length > 0;
  const hasGeography = Array.isArray(geography) && geography.length > 0;
  const hasRole = Array.isArray(role) && role.length > 0;

  if (hasIndustry) {
    const selections = buildSelectionsFromIndustry(industry);
    if (selections.length === 0) return null;
    const primarySelections: PrimarySelection[] = selections.map((sel) => ({
      ...sel,
      isActive: true,
    }));
    const definition = getFilterDefinition(PRIMARY_INDUSTRY_FILTER_ID);
    if (!definition || definition.type !== FilterType.TREE) return null;
    const selectionListIds =
      (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
    const result = {
      ...definition,
      id: PRIMARY_INDUSTRY_FILTER_ID,
      name: definition.name,
      type: FilterType.TREE,
      selectionListIds,
      selections,
      primarySelections,
      // Screener v2 has no `tabContent.primaryFilter`; the filter chip row
      // (`useFilterTags`) only shows filters with isPrimary false. "Primary"
      // in the AI payload names the scoping axis, not CustomDash's primary bar.
      isPrimary: false,
      isUserSubmitted: true,
    } as TreeFilter;
    return result;
  }

  if (hasGeography) {
    const selections = buildSelectionsFromGeography(geography);
    if (selections.length === 0) return null;
    const primarySelections: PrimarySelection[] = selections.map((sel) => ({
      ...sel,
      isActive: true,
    }));
    const definition = getFilterDefinition(PRIMARY_GEOGRAPHY_FILTER_ID);
    if (!definition || definition.type !== FilterType.TREE) return null;
    const selectionListIds =
      (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
    return {
      ...definition,
      id: PRIMARY_GEOGRAPHY_FILTER_ID,
      name: definition.name,
      type: FilterType.TREE,
      selectionListIds,
      selections,
      primarySelections,
      isPrimary: false,
      isUserSubmitted: true,
    } as TreeFilter;
  }

  if (hasRole) {
    const selections = buildSelectionsFromRole(role);
    if (selections.length === 0) return null;
    const primarySelections: PrimarySelection[] = selections.map((sel) => ({
      ...sel,
      isActive: true,
    }));
    const definition = getFilterDefinition(PRIMARY_ROLE_FILTER_ID);
    if (!definition || definition.type !== FilterType.TREE) return null;
    const selectionListIds =
      (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
    return {
      ...definition,
      id: PRIMARY_ROLE_FILTER_ID,
      name: definition.name,
      type: FilterType.TREE,
      selectionListIds,
      selections,
      primarySelections,
      isPrimary: false,
      isUserSubmitted: true,
    } as TreeFilter;
  }

  return null;
};
